import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ChevronDown } from "lucide-react";
import React from "react";

interface ActionButtonItem {
  text: string;
  onClick: () => void;
  icon: React.ReactNode;
  disabled?: boolean;
}

interface SplitActionButtonProps {
  disabled?: boolean;
  actionButtons: ActionButtonItem[];
  anchor?: string;
  buttonClassName?: string;
}

const SplitActionButton = ({
  disabled,
  actionButtons,
  anchor = "top end",
  buttonClassName = "",
}: SplitActionButtonProps) => {
  const hasActions = Array.isArray(actionButtons) && actionButtons.length > 0;
  const [primaryIndex, setPrimaryIndex] = React.useState(0);
  const userSelectedRef = React.useRef(false);

  React.useEffect(() => {
    if (!hasActions) {
      setPrimaryIndex(0);
      userSelectedRef.current = false;
      return;
    }
    if (primaryIndex >= actionButtons.length) {
      setPrimaryIndex(0);
      userSelectedRef.current = false;
    }
  }, [actionButtons, hasActions, primaryIndex]);

  React.useEffect(() => {
    if (hasActions && !userSelectedRef.current) {
      setPrimaryIndex(0);
    }
  }, [hasActions, actionButtons]);

  const primaryAction = React.useMemo<ActionButtonItem | null>(() => {
    if (!hasActions) return null;
    return actionButtons[Math.min(primaryIndex, actionButtons.length - 1)];
  }, [actionButtons, hasActions, primaryIndex]);

  const menuItems = React.useMemo(() => {
    if (!hasActions) return [] as ActionButtonItem[];
    return actionButtons.filter((_, idx) => idx !== primaryIndex);
  }, [actionButtons, hasActions, primaryIndex]);

  const renderPrimaryIcon = React.useCallback(() => {
    const node = primaryAction?.icon as any;
    if (!node) return null;
    if (React.isValidElement(node)) {
      const prevClass = ((node as React.ReactElement<any>).props?.className || "").toString();
      return React.cloneElement(node as React.ReactElement<any>, {
        className: `${prevClass} !text-white`,
      });
    }
    return node;
  }, [primaryAction]);

  if (!hasActions) {
    return (
      <button
        className={`flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 cursor-pointer ${buttonClassName}`}
        onClick={primaryAction?.onClick}
        disabled={disabled}
      >
        {primaryAction?.icon}
        {primaryAction?.text}
      </button>
    );
  }

  return (
    <Menu as="div" className="relative inline-flex z-20">
      <button
        className={`flex items-center gap-2 pl-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 cursor-pointer ${buttonClassName}`}
        onClick={() => primaryAction?.onClick?.()}
        disabled={disabled || !primaryAction || primaryAction.disabled}
        aria-label={typeof primaryAction?.text === "string" ? primaryAction?.text : undefined}
      >
        {renderPrimaryIcon()}
        {primaryAction?.text}
        <div className="flex items-center">
          <span className="h-5 border-1 border-white/40"></span>
          <MenuButton
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            as="span"
            className="inline-flex items-center pl-2 pr-3 py-2.5"
            disabled={disabled || menuItems.length === 0}
            aria-label="Open actions"
          >
            <ChevronDown className="w-4 h-4" />
          </MenuButton>
        </div>
      </button>
      <MenuItems
        transition
        anchor={anchor as any}
        className="z-50 w-auto rounded-lg border border-gray-200 bg-white p-1 transition duration-100 ease-out [--anchor-gap:0px] focus:outline-none data-closed:scale-95 data-closed:opacity-0"
      >
        {menuItems.map((item, idx) => (
          <MenuItem key={`${item.text}-${idx}`} as="div" className="w-full">
            <button
              onClick={() => {
                item.onClick?.();
                const absoluteIdx = actionButtons.findIndex((ab) => ab === item);
                setPrimaryIndex(absoluteIdx >= 0 ? absoluteIdx : 0);
                userSelectedRef.current = true;
              }}
              className="group flex w-full items-center gap-2 rounded-lg px-3 py-1.5 whitespace-nowrap transition-colors hover:bg-gray-100 data-focus:bg-gray-200"
              disabled={item.disabled}
            >
              {item.icon}
              <span className="text-[14px] leading-5 font-medium text-gray-700 group-hover:text-gray-900 truncate">
                {item.text}
              </span>
            </button>
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  );
};

export default SplitActionButton;
